import { useState, useEffect, useCallback } from 'react';
import { fetchEmpreendimentos, fetchUnidades, clearEmpreendimentosCache } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { useAlertHelpers } from '../components/layout/Alert/AlertHelpers';
 
export const useFlexPropertySelection = (isMRV) => {
  const [loading, setLoading] = useState(true);
  const [loadingUnidades, setLoadingUnidades] = useState(false);
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [blocos, setBlocos] = useState([]);
  const [unidades, setUnidades] = useState([]);
 
  const [selectedEstado, setSelectedEstado] = useState('');
  const [selectedCidade, setSelectedCidade] = useState('');
  const [selectedEmpreendimento, setSelectedEmpreendimento] = useState('');
  const [selectedBloco, setSelectedBloco] = useState('');
  const [selectedUnidade, setSelectedUnidade] = useState('');
 
  const [valorTabela, setValorTabela] = useState('');
  const [isPagadoria, setIsPagadoria] = useState(false);
  const [isRendaExterior, setIsRendaExterior] = useState(false);
  const [selectedUnidadeData, setSelectedUnidadeData] = useState(null);
  const [selectedEmpreendimentoData, setSelectedEmpreendimentoData] = useState(null);
 
  const [allEmpreendimentos, setAllEmpreendimentos] = useState([]);
  const [unitsCache, setUnitsCache] = useState({});
 
  const { showError } = useAlertHelpers();
 
  // 1. Carregar empreendimentos e extrair estados unicos
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Limpar cache da API para forçar nova requisição
        clearEmpreendimentosCache(isMRV, 'Flex');
        
        const data = await fetchEmpreendimentos(isMRV, 'Flex');
        const uniqueData = [...new Map(data.map(emp => [emp.id, emp])).values()];
        setAllEmpreendimentos(uniqueData);
        // Limpar cache de unidades ao mudar marca
        setUnitsCache({});
 
        // Extrair estados únicos
        const uniqueStates = [...new Set(
          uniqueData.map(emp => emp.cidade?.unidadeFederativa).filter(Boolean)
        )].sort((a, b) => a.localeCompare(b));
       
        setEstados(uniqueStates.map(uf => ({ value: uf, label: uf })));
        
        // Inicializar lista de empreendimentos com todos os empreendimentos disponíveis
        const todosEmpreendimentos = uniqueData
          .sort((a, b) => a.nome.localeCompare(b.nome));
        
        setEmpreendimentos(todosEmpreendimentos.map(emp => ({
          value: emp.id,
          label: emp.nome.toUpperCase(),
          data: emp
        })));
      } catch (error) {
        console.error('Erro ao obter empreendimentos:', error);
        showError('Erro ao obter empreendimentos!', { title: 'Erro', duration: 4000 });
      } finally {
        setLoading(false);
      }
    };
 
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMRV]);

  // 1.1. Limpar seleções quando a marca mudar
  useEffect(() => {
    setSelectedEstado('');
    setSelectedCidade('');
    setSelectedEmpreendimento('');
    setSelectedBloco('');
    setSelectedUnidade('');
    setValorTabela('');
    setSelectedUnidadeData(null);
    setSelectedEmpreendimentoData(null);
  }, [isMRV]);

  // 2. Filtrar cidades quando estado muda
  useEffect(() => {
    if (!selectedEstado) {
      setCidades([]);
      return;
    }

    const empreendimentosFiltrados = allEmpreendimentos
      .filter(emp => emp.cidade?.unidadeFederativa === selectedEstado);

    const cidadesFiltradas = empreendimentosFiltrados
      .map(emp => emp.cidade)
      .filter((cidade, index, self) =>
        cidade && self.findIndex(c => c.id === cidade.id) === index
      )
      .sort((a, b) => a.nome.localeCompare(b.nome));
 
    setCidades(cidadesFiltradas.map(cidade => ({
      value: cidade.id,
      label: cidade.nome.toUpperCase()
    })));
  }, [selectedEstado, allEmpreendimentos]);
 
  // 3. Atualizar lista de empreendimentos (todos ou filtrados por cidade)
  useEffect(() => {
    // Se não há cidade selecionada, mostra todos os empreendimentos
    if (!selectedCidade) {
      const todosEmpreendimentos = allEmpreendimentos
        .sort((a, b) => a.nome.localeCompare(b.nome));
      
      setEmpreendimentos(todosEmpreendimentos.map(emp => ({
        value: emp.id,
        label: emp.nome.toUpperCase(),
        data: emp
      })));
      return;
    }
 
    // Se há cidade selecionada, filtra empreendimentos por cidade
    const empreendimentosDaCidade = allEmpreendimentos
      .filter(emp => emp.cidade?.id === selectedCidade)
      .sort((a, b) => a.nome.localeCompare(b.nome));
 
    setEmpreendimentos(empreendimentosDaCidade.map(emp => ({
      value: emp.id,
      label: emp.nome.toUpperCase(),
      data: emp
    })));
  }, [selectedCidade, allEmpreendimentos]);
 
  // 4.1. Atualizar dados do empreendimento quando selecao muda
  useEffect(() => {
    if (!selectedEmpreendimento) {
      setSelectedEmpreendimentoData(null);
      return;
    }

    const empData = empreendimentos.find(emp => emp.value === selectedEmpreendimento)?.data;
    setSelectedEmpreendimentoData(empData);
  }, [selectedEmpreendimento, empreendimentos]);

  // 5. Carregar unidades quando empreendimento muda
  useEffect(() => {
    if (!selectedEmpreendimento) return;
 
    const loadUnidades = async () => {
      setLoadingUnidades(true);
      try {
        let unidadesData;
       
        if (unitsCache[selectedEmpreendimento]) {
          unidadesData = unitsCache[selectedEmpreendimento];
        } else {
          unidadesData = await fetchUnidades(selectedEmpreendimento, isMRV, 'Flex');
          setUnitsCache(prev => ({
            ...prev,
            [selectedEmpreendimento]: unidadesData
          }));
        }
 
        if (!unidadesData || unidadesData.length === 0) {
          setBlocos([]);
          setUnidades([]);
          setSelectedBloco('');
          setSelectedUnidade('');
          return;
        }
 
        // Extrair blocos únicos do campo bloco.nome ou do nome da unidade como fallback
        const blocosUnicos = [...new Set(
          unidadesData
            .map(u => {
              // Tentar obter do campo bloco.nome primeiro
              if (u.bloco?.nome) {
                return u.bloco.nome;
              }
              // Fallback: extrair do nome da unidade usando regex
              const matchBloco = u.nome.match(/BLOCO\s+(\d+)/i);
              if (matchBloco) {
                return `BLOCO ${matchBloco[1]}`;
              }
              const matchTorre = u.nome.match(/TORRE\s+(\d+)/i);
              if (matchTorre) {
                return `TORRE ${matchTorre[1]}`;
              }
              return null;
            })
            .filter(Boolean)
        )].sort((a, b) => {
          // Tentar extrair números para ordenação numérica
          const numA = a.match(/\d+/);
          const numB = b.match(/\d+/);
          if (numA && numB) {
            return Number.parseInt(numA[0], 10) - Number.parseInt(numB[0], 10);
          }
          // Fallback para ordenação alfabética
          return a.localeCompare(b);
        });
 
        setBlocos(blocosUnicos.map(bloco => ({ value: bloco, label: bloco })));
 
        // Se houver apenas um bloco, selecione automaticamente
        if (blocosUnicos.length === 1) {
          setSelectedBloco(blocosUnicos[0]);
        }
      } catch (error) {
        console.error('Erro ao carregar unidades:', error);
        showError('Erro ao carregar unidades!', { title: 'Erro', duration: 4000 });
      } finally {
        setLoadingUnidades(false);
      }
    };
 
    loadUnidades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmpreendimento, isMRV]);
 
  // 6. Filtrar unidades quando bloco muda
  useEffect(() => {
    if (!selectedBloco || !unitsCache[selectedEmpreendimento]) {
      setUnidades([]);
      setSelectedUnidade('');
      return;
    }
 
    const unidadesDoBloco = unitsCache[selectedEmpreendimento]
      .filter(u => {
        // Tentar comparar com bloco.nome primeiro
        if (u.bloco?.nome) {
          return u.bloco.nome === selectedBloco;
        }
        // Verifica se o nome começa com o bloco selecionado seguido de espaço ou é o bloco exato
        const blocoPattern = new RegExp(`^${selectedBloco}\\s|^${selectedBloco}$`, 'i');
        return blocoPattern.test(u.nome);
      })
      .sort((a, b) => a.nome.localeCompare(b.nome));
 
    setUnidades(unidadesDoBloco.map(u => ({
      value: u.id,
      label: u.nome.toUpperCase(),
      data: u
    })));
 
    setSelectedUnidade('');
  }, [selectedBloco, selectedEmpreendimento, unitsCache]);
 
  // 7. Atualizar valor da tabela e dados da unidade quando unidade muda
  useEffect(() => {
    if (!selectedUnidade) {
      setValorTabela('');
      setSelectedUnidadeData(null);
      return;
    }
 
    // Procurar nos dados em cache do empreendimento selecionado
    const unidadesDataFromCache = unitsCache[selectedEmpreendimento];
    const unidadeData = unidadesDataFromCache?.find(u => u.id === selectedUnidade);
    setSelectedUnidadeData(unidadeData || null);
    
    if (unidadeData?.unidadePreco?.valorImovelComDelta) {
      setValorTabela(formatCurrency(unidadeData.unidadePreco.valorImovelComDelta));
    }
  }, [selectedUnidade, selectedEmpreendimento, unitsCache]);
 
  // Handlers
  const handleEstadoChange = useCallback((estado) => {
    setSelectedEstado(estado);
  }, []);
 
  const handleCidadeChange = useCallback((cidade) => {
    setSelectedCidade(cidade);
  }, []);
 
  const handleEmpreendimentoChange = useCallback((empreendimento) => {
    setSelectedEmpreendimento(empreendimento);
    
    // Se um empreendimento foi selecionado, preencher automaticamente estado e cidade
    if (empreendimento) {
      const empData = allEmpreendimentos.find(emp => emp.id === empreendimento);
      if (empData?.cidade) {
        setSelectedEstado(empData.cidade.unidadeFederativa);
        setSelectedCidade(empData.cidade.id);
      }
    }
  }, [allEmpreendimentos]);
 
  const handleBlocoChange = useCallback((bloco) => {
    setSelectedBloco(bloco);
  }, []);
 
  const handleUnidadeChange = useCallback((unidade) => {
    setSelectedUnidade(unidade);
  }, []);

  const handlePagadoriaChange = useCallback((e) => {
    const value = typeof e === 'boolean' ? e : e.target.checked;
    setIsPagadoria(value);
  }, []);

  const handleRendaExteriorChange = useCallback((e) => {
    const value = typeof e === 'boolean' ? e : e.target.checked;
    setIsRendaExterior(value);
  }, []);

  const handleClearEmpreendimento = useCallback(() => {
    setSelectedEmpreendimento('');
    setSelectedEstado('');
    setSelectedCidade('');
    setBlocos([]);
    setUnidades([]);
    setSelectedBloco('');
    setSelectedUnidade('');
  }, []);
 
  return {
    loading,
    loadingUnidades,
    estados,
    cidades,
    empreendimentos,
    blocos,
    unidades,
    selectedEstado,
    selectedCidade,
    selectedEmpreendimento,
    selectedBloco,
    selectedUnidade,
    valorTabela,
    isPagadoria,
    isRendaExterior,
    selectedUnidadeData,
    selectedEmpreendimentoData,
    handleEstadoChange,
    handleCidadeChange,
    handleEmpreendimentoChange,
    handleBlocoChange,
    handleUnidadeChange,
    handlePagadoriaChange,
    handleRendaExteriorChange,
    handleClearEmpreendimento
  };
};